import { MessageSquare, Activity, Send } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { cx } from "../lib/cx"
import type { TaskComment, TaskActivityEntry } from "../types"

type Props = {
  taskId: string
}

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHrs = Math.floor(diffMins / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const describeActivity = (a: TaskActivityEntry) => {
  if (a.action === "commented") return `${a.userName || "Someone"} commented`
  if (a.action === "updated" && a.field) {
    const fieldLabel = a.field.replace("_", " ")
    return `${a.userName || "Someone"} changed ${fieldLabel} from "${a.oldValue || "none"}" to "${a.newValue || "none"}"`
  }
  return `${a.userName || "Someone"} ${a.action}`
}

const TaskCommentsSection = ({ taskId }: Props) => {
  const [tab, setTab] = useState<"comments" | "activity">("comments")
  const [comments, setComments] = useState<TaskComment[]>([])
  const [activity, setActivity] = useState<TaskActivityEntry[]>([])
  const [newComment, setNewComment] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const fetchComments = useCallback(async () => {
    try {
      const res = await fetch(`/api/tasks/${taskId}/comments`)
      if (res.ok) {
        const data = await res.json()
        setComments(data.comments || [])
      }
    } catch { /* ok */ }
  }, [taskId])

  const fetchActivity = useCallback(async () => {
    try {
      const res = await fetch(`/api/tasks/${taskId}/activity`)
      if (res.ok) {
        const data = await res.json()
        setActivity(data.activity || [])
      }
    } catch { /* ok */ }
  }, [taskId])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchComments()
      void fetchActivity()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [fetchActivity, fetchComments])

  const handleSend = async () => {
    if (!newComment.trim()) return
    setIsLoading(true)
    try {
      const res = await fetch(`/api/tasks/${taskId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: newComment.trim() }),
      })
      if (res.ok) {
        const data = await res.json()
        setComments((prev) => [...prev, data.comment])
        setNewComment("")
        fetchActivity()
      }
    } catch { /* ok */ }
    setIsLoading(false)
  }

  return (
    <div>
      {/* Tabs */}
      <div className="mb-3 flex gap-1">
        <button
          type="button"
          onClick={() => setTab("comments")}
          className={cx(
            "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition",
            tab === "comments" ? "bg-moss text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200",
          )}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Comments ({comments.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("activity")}
          className={cx(
            "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition",
            tab === "activity" ? "bg-moss text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200",
          )}
        >
          <Activity className="h-3.5 w-3.5" />
          Activity ({activity.length})
        </button>
      </div>

      {tab === "comments" && (
        <div className="space-y-3">
          {/* Comment list */}
          {comments.length > 0 ? (
            <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-line bg-slate-50 p-3">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-moss/10 text-xs font-medium text-moss">
                    {c.userName?.[0] || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-900">{c.userName}</span>
                      <span className="text-xs text-stone">{formatTime(c.createdAt)}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-slate-700 whitespace-pre-wrap">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-line bg-slate-50 p-4 text-center text-xs text-stone">
              No comments yet
            </div>
          )}

          {/* New comment input */}
          <div className="flex gap-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              placeholder="Write a comment..."
              className="flex-1 resize-none rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
              rows={2}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isLoading || !newComment.trim()}
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-moss text-white hover:bg-moss/90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {tab === "activity" && (
        <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-line bg-slate-50 p-3">
          {activity.length > 0 ? (
            activity.map((a) => (
              <div key={a.id} className="flex items-start gap-2 text-xs">
                <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                <div className="flex-1">
                  <p className="text-slate-700">{describeActivity(a)}</p>
                  <p className="text-stone">{formatTime(a.createdAt)}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="py-4 text-center text-xs text-stone">No activity yet</p>
          )}
        </div>
      )}
    </div>
  )
}

export default TaskCommentsSection
